import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClearOutlined,
  EllipsisOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ProjectOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import { ColDef, Column, ColumnPinnedType } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Button, ButtonProps, Drawer, Dropdown } from 'antd';
import { observer } from 'mobx-react-lite';

import { DataGrid, useDataGridApi } from 'app/common/ui/data-grid';

import styles from './columns-settings.module.css';

const colDef: ColDef[] = [
  {
    colId: 'visibility',

    headerComponent: () => <EyeOutlined />,
    headerClass: 'ag-grid-header-cell-center',
    width: 40,
    cellClass: styles.cellButton,
    cellRenderer: observer((props: CustomCellRendererProps) => {
      const getGridApi = useDataGridApi();
      const columnInstance = props.data as Column;
      const [colVisibility, setColVisibility] = useState(() =>
        columnInstance.isVisible(),
      );

      const toogleVisibility = async () => {
        const gridApi = await getGridApi();
        const currentState = columnInstance.isVisible();

        gridApi.setColumnVisible(columnInstance, !currentState);
      };

      useEffect(() => {
        const subscribeColVisibility = () => {
          setColVisibility(columnInstance.isVisible());
        };

        columnInstance.addEventListener(
          'visibleChanged',
          subscribeColVisibility,
        );

        return () => {
          columnInstance.removeEventListener(
            'visibleChanged',
            subscribeColVisibility,
          );
        };
      }, [columnInstance]);

      return (
        <Button
          block
          type="text"
          onClick={toogleVisibility}
          style={{ height: '100%' }}
          icon={colVisibility ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        />
      );
    }),
  },
  {
    colId: 'pin',
    headerComponent: () => (
      <PushpinOutlined style={{ transform: 'rotate(-45deg)' }} />
    ),
    headerClass: 'ag-grid-header-cell-center',
    cellClass: styles.cellButton,
    width: 40,
    cellRenderer: observer((props: CustomCellRendererProps) => {
      const columnInstance = props.data as Column;
      const [pinState, setPinState] = useState(() =>
        columnInstance.getPinned(),
      );
      const getGridApi = useDataGridApi();

      /*
       * ┌──► null | false ───► left | true ───► right ───┐
       * │                                                │
       * └────────────────────────────────────────────────┘
       */
      const getNextPinState = useCallback((pinState: ColumnPinnedType) => {
        switch (pinState) {
          case null:
          case false:
            return 'left';
          case 'left':
            return 'right';
          case 'right':
          case true:
          default:
            return null;
        }
      }, []);

      const pinRotation = useMemo(() => {
        switch (pinState) {
          case 'left':
          case true:
            return 'rotate(0deg)';
          case 'right':
            return 'rotate(-90deg)';
          case null:
          case false:
          default:
            return 'rotate(-45deg)';
        }
      }, [pinState]);

      const tooglePin = async () => {
        const gridApi = await getGridApi();
        const currentState = columnInstance.getPinned();
        const nextState = getNextPinState(currentState);

        gridApi.setColumnPinned(columnInstance, nextState);
      };

      const isInactive = !pinState;

      useEffect(() => {
        const subscribeToPin = async () => {
          const gridApi = await getGridApi();

          gridApi.addEventListener('columnPinned', () => {
            setPinState(columnInstance.getPinned());
          });
        };

        subscribeToPin();
      }, [columnInstance, getGridApi]);

      return (
        <Button
          block
          type="text"
          onClick={tooglePin}
          style={{ height: '100%' }}
          icon={
            <PushpinOutlined
              style={{ transform: pinRotation, opacity: isInactive ? 0.3 : 1 }}
            />
          }
        />
      );
    }),
  },
  {
    headerName: 'Колонка',
    field: 'userProvidedColDef.headerName',
  },
];

type ColumnsSettingsProps = ButtonProps;
export const ColumnsSettings = observer((props: ColumnsSettingsProps) => {
  const dataGridApi = useDataGridApi();
  const [cols, setCols] = useState<Column[]>([]);
  const [openDrawer, setOpenDrawer] = useState(false);

  const handleClear = async () => {
    const api = await dataGridApi();
    const colsState = api.getColumnState();

    api.applyColumnState({
      state: colsState.map((col) => ({
        ...col,
        hide: false,
        pinned: null,
      })),
    });
  };

  const handlePositionsReset = async () => {
    const api = await dataGridApi();
    const colsState = api.getColumns();
    if (!colsState) return;

    api.moveColumns(colsState, 0);
  };

  useEffect(() => {
    async function getColumns() {
      const gridApi = await dataGridApi();
      const cols = gridApi.getColumns();

      if (cols === null) return;
      setCols(cols);
    }

    getColumns();
  }, [dataGridApi]);

  return (
    <>
      <Dropdown.Button
        buttonsRender={() => [
          <Button
            onClick={() => setOpenDrawer(true)}
            icon={<ProjectOutlined style={{ color: '#2664CD' }} />}
          >
            Колонки
          </Button>,
          <Button type={'default'} icon={<EllipsisOutlined />} />,
        ]}
        menu={{
          items: [
            {
              key: '1',
              label: 'Сбросить порядок ',
              icon: <ClearOutlined />,
              onClick: handlePositionsReset,
            },
          ],
          // onClick: onResetAll,
        }}
        style={props.style}
      />

      <Drawer
        title="Настройки колонок"
        open={openDrawer}
        mask={false}
        footer={
          <>
            <div style={{ display: 'flex', gap: 16 }}>
              <Button block onClick={handleClear}>
                Сбросить все
              </Button>
            </div>
          </>
        }
        onClose={() => setOpenDrawer(false)}
      >
        <DataGrid
          className={styles.columnSettings}
          autoSizeStrategy={{ type: 'fitGridWidth' }}
          rowData={cols}
          columnDefs={colDef}
        />
      </Drawer>
    </>
  );
});
